import { useEffect, useState } from 'react';
import { useCreate, useList } from '@refinedev/core';
import { useNavigate } from 'react-router';
import type { CreatePromptBody, TfTrackRow } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function PromptsCreate() {
  const navigate = useNavigate();
  const { mutate, mutation } = useCreate();
  const isPending = mutation.isPending;
  const { result: tracks } = useList<TfTrackRow>({
    resource: 'tracks',
    pagination: { mode: 'off' },
  });

  const [trackId, setTrackId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [inputs, setInputs] = useState('');

  // Default the track-id picker to the first track once the list resolves.
  useEffect(() => {
    if (!trackId && tracks?.data?.length) setTrackId(tracks.data[0]?.id ?? '');
  }, [trackId, tracks?.data]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackId) return;
    const body: CreatePromptBody = {
      trackId,
      name: name.trim(),
      content,
    };
    if (description.trim()) body.description = description.trim();
    const inputArr = inputs.split(',').map((s) => s.trim()).filter(Boolean);
    if (inputArr.length) body.inputs = inputArr;
    mutate(
      { resource: 'prompts', values: body },
      { onSuccess: () => navigate('/admin/prompts') },
    );
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New prompt</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Prompt</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="trackId">Track</Label>
              <select
                id="trackId"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={trackId}
                onChange={(e) => setTrackId(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select a track…
                </option>
                {tracks?.data?.map((t: TfTrackRow) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input id="description" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <textarea
                id="content"
                required
                className="min-h-40 w-full rounded-md border bg-background px-3 py-2 font-mono text-xs"
                placeholder="You are a helpful assistant for {{trackName}}…"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inputs">Inputs (comma separated)</Label>
              <Input
                id="inputs"
                value={inputs}
                placeholder="trackName, userRole"
                onChange={(e) => setInputs(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isPending || !trackId}>
                {isPending ? 'Creating…' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/prompts')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
